import { createApi } from '@reduxjs/toolkit/query/react';
import * as Sentry from '@sentry/react';
import { callWithRequestLimiter } from '_services/Api/_utils/callWithRequestLimiter';
import Api from '_services/Api/index';
import OpenCheckoutPlan from '_services/LocalForage/OpenCheckoutPlan';
import { isUserLoggedIn } from '_services/LocalStorage/LoggedIn';
import { UserShape } from '_types/User';
import { AxiosResponse } from 'axios';

const baseRoute = 'membership/';

export type getCheckoutUrlDataShape = {
    product_id?: string;
    interval?: string;
    return_url?: string;
    designer_code?: string;
    trial?: boolean;
    trialDuration?: number;
    success_url?: string;
    currency?: string;
    discount?: boolean | string;
    fbp?: string | null;
    fbc?: string | null;
    event_id?: string | null;
    client_ip?: string | null;
    user_agent?: string | null;
    referer?: string | null;
    utmParams?: {
        utm_source?: string;
        utm_medium?: string;
        utm_campaign?: string;
        utm_term?: string;
        utm_content?: string;
    };
};

export type GetCheckoutUrlResponseShape = {
    url: string;
    meta_event_id?: string | null;
};

export type SubscriptionSwapInvoiceShape = {
    amount_due: number;
    amount_remaining: number;
    subtotal: number;
    total: number;
    currency: string;
    lines: {
        data: {
            amount: number;
            period: {
                start: number;
                end: number;
            };
            description: string;
            currency: string;
            quantity: number;
        }[];
    };
    starting_balance: number;
};

export type CheckTrialEligibilityResponseShape = {
    eligible_for_trial: boolean;
    has_had_subscription: boolean;
    has_had_trial: boolean;
    has_active_subscription: boolean;
    trial_active: boolean;
    trial_ends_at: string | null;
};

export const SCA_STATUS = 'requires_action';

export type MembershipCancelSuccess = {
    status: 'success';
    user: UserShape;
};

export type MembershipCancelError = {
    message: string;
};

export type MembershipCancelSwapResponse =
    | MembershipCancelSuccess
    | MembershipCancelError;

type StripeStatus = 'trialing' | 'active' | 'past_due' | 'canceled' | 'unpaid';

export type MembershipStatusResponse = {
    stripe_status: StripeStatus;
    cancels_at_if_unpaid: string | null;
    trial_ends_at?: string | null;
    current_period_end?: string | null;
    ends_at?: string | null;
    next_payment_attempt?: string | null;
    next_amount_due?: number;
    next_amount_currency?: string;
    next_amount_original?: number;
    next_payment?: {
        interval: string;
    };
};

const MembershipApi = {
    /**
     * Gets checkout URL for subscription
     *
     * @param data - checkout data
     * @returns Promise<AxiosResponse>
     */
    async getCheckoutUrl(
        data: getCheckoutUrlDataShape,
    ): Promise<AxiosResponse<GetCheckoutUrlResponseShape>> {
        const route = baseRoute + 'get-checkout-url';

        let membership = {};
        try {
            const storedMembership = await OpenCheckoutPlan.get();
            membership = JSON.parse(storedMembership || `{}`);
        } catch (e) {
            Sentry.captureException(e);
        }

        return Api.post(route, {
            ...membership,
            ...data,
            trial: data.trial ?? false,
        });
    },

    async getBillingPortalUrl(data: {
        return_url: string;
    }): Promise<AxiosResponse<{ url: string }>> {
        const route = baseRoute + 'get-portal-url';

        return Api.post(route, data);
    },

    /**
     * For upgrading or downgrading subscriptions
     * @returns {Promise<AxiosResponse>}
     */
    async changeSubscription(data: {
        product: string;
        interval: string;
    }): Promise<
        AxiosResponse<{ user?: UserShape; status?: string; url: string }>
    > {
        const route = baseRoute + 'change-subscription';

        return Api.post(route, data);
    },

    /**
     * For upgrading or downgrading subscriptions
     * @returns {Promise<AxiosResponse>}
     */
    async getSubscriptionSwapInvoice(data: {
        product: string;
        interval: string;
    }): Promise<
        AxiosResponse<{
            status: SubscriptionSwapInvoiceShape;
        }>
    > {
        const route = baseRoute + 'get-upcoming-price-details';

        return Api.post(route, data);
    },

    /**
     * Checks if user is eligible for a trial
     * @returns {Promise<AxiosResponse>}
     */
    async checkTrialEligibility(): Promise<
        AxiosResponse<CheckTrialEligibilityResponseShape>
    > {
        const route = baseRoute + 'check-trial-eligibility';

        return Api.post(route);
    },

    /**
     * Checks if user is eligible for a trial
     * @returns {Promise<>}
     */
    async cancelSubscription(
        steps?: string,
        reason?: string,
    ): Promise<AxiosResponse<MembershipCancelSuccess>> {
        const route = baseRoute + 'cancel';

        return Api.post(route, {
            content: steps,
            subscriptionReason: reason,
        });
    },

    async membershipStatus(): Promise<AxiosResponse<MembershipStatusResponse>> {
        const route = baseRoute + 'status';

        return Api.get(route);
    },

    async swapInterval(
        shouldGiveDiscount: boolean,
    ): Promise<AxiosResponse<MembershipCancelSwapResponse>> {
        const route = baseRoute + 'swap-interval';

        return Api.post(route, { discount: shouldGiveDiscount });
    },
};

// RTK Query API for membership-related operations

export const MembershipQueryApi = createApi({
    reducerPath: 'membership/api',
    tagTypes: [
        'TrialEligibility',
        'MembershipStatus',
        'CheckoutUrl',
        'BillingPortal',
    ],
    baseQuery: callWithRequestLimiter,
    endpoints: (builder) => ({
        checkTrialEligibility: builder.query<
            CheckTrialEligibilityResponseShape,
            void
        >({
            query: () => ({
                url: 'membership/check-trial-eligibility',
                method: 'POST',
            }),
            providesTags: ['TrialEligibility'],
        }),
        membershipStatus: builder.query<MembershipStatusResponse, void>({
            query: () => ({
                url: 'membership/status',
                method: 'GET',
            }),
            providesTags: ['MembershipStatus'],
        }),
        reportTrialStarted: builder.mutation<void, void>({
            query: () => ({
                url: 'membership/report-trial-started',
                method: 'POST',
            }),
        }),
    }),
});

const {
    useCheckTrialEligibilityQuery,
    useLazyCheckTrialEligibilityQuery,
    useReportTrialStartedMutation,
    useMembershipStatusQuery: useMembershipStatusQueryBase,
    useLazyMembershipStatusQuery,
} = MembershipQueryApi;

export {
    useCheckTrialEligibilityQuery,
    useLazyCheckTrialEligibilityQuery,
    useLazyMembershipStatusQuery,
    useReportTrialStartedMutation,
};

export const useMembershipStatusQuery = (
    options?: Parameters<typeof useMembershipStatusQueryBase>[1],
): ReturnType<typeof useMembershipStatusQueryBase> => {
    const shouldSkip = !isUserLoggedIn() || Boolean(options?.skip);

    return useMembershipStatusQueryBase(undefined, {
        ...options,
        skip: shouldSkip,
    });
};

export default MembershipApi;
